import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { postAPI, notificationAPI, channelAPI } from '../../service/api';
import {
  INIT_POST,
  INIT_MY_POST,
  SET_SELECTED_POST,
  CREATE_POST,
  DELETE_POST,
  UPDATE_POST,
  CREATE_COMMENT,
  DELETE_COMMENT,
  CHANGE_USERNAME,
  DELETE_LIKE,
  CREATE_LIKE,
} from './types';
import reducer from './reducer';
import { Modal } from '../../components/templates';

const initialPost = {
  channelId: '',
  posts: [],
  myPosts: [],
  selectedPostId: '',
  selectedPost: {},
};

const PostContext = createContext(initialPost);

export const usePostContext = () => useContext(PostContext);

const channelName = 'Quritor';
const channelId = '6182c2ce41316567ffd9bcaa';
const LIMIT = 8;

const PostProvider = ({ children }) => {
  const history = useHistory();
  const [modalVisible, setModalVisible] = useState(false);

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const [post, dispatch] = useReducer(reducer, initialPost);
  const [offset, setOffset] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleGetPosts = useCallback(async () => {
    try {
      const { posts } = await channelAPI.getChannelDetail(channelName);
      const done = posts.length <= offset;
      if (!done && !isLoading) {
        setIsLoading(true);
        const posts = await postAPI.getPosts({
          offset,
          limit: LIMIT,
          channelId,
        });
        setOffset(offset + LIMIT);
        setIsLoading(false);
        dispatch({ type: INIT_POST, payload: posts });
      }
    } catch (e) {
      console.log(e);
    }
  }, [offset, isLoading]);

  const handleGetMyPosts = useCallback(async (userId) => {
    setIsLoading(true);

    const myPosts = await postAPI.getMyPosts(userId);

    dispatch({
      type: INIT_MY_POST,
      payload: myPosts.filter((post) => post.channel._id === channelId),
    });

    setIsLoading(false);

    dispatch({ type: INIT_POST, payload: myPosts });
  }, []);

  const handleSetSelectedPost = useCallback(async (postId) => {
    const post = await postAPI.getPost(postId);

    dispatch({ type: SET_SELECTED_POST, payload: { post } });
  }, []);

  const handleUpdatePost = useCallback(async (data) => {
    const updatedPost = await postAPI.updatePost({
      ...data,
      channelId,
    });

    dispatch({
      type: UPDATE_POST,
      payload: {
        updatedPost,
      },
    });
  }, []);

  const handleCreatePost = useCallback(async (data) => {
    const createdPost = await postAPI.addPost(data);

    openModal();
    dispatch({ type: CREATE_POST, payload: { createdPost } });
  }, []);

  const handleDeletePost = useCallback(
    async (postId) => {
      await postAPI.deletePost(postId);

      dispatch({
        type: DELETE_POST,
        payload: {
          postId,
        },
      });

      history.replace('/');
    },
    [history],
  );

  const handleCreateComment = useCallback(async (data, targetUserId) => {
    const createdComment = await postAPI.createComment(data);

    await notificationAPI.createNotification({
      notificationType: 'COMMENT',
      notificationTypeId: createdComment._id,
      userId: targetUserId,
      postId: data.postId,
    });

    dispatch({
      type: CREATE_COMMENT,
      payload: {
        createdComment,
      },
    });
  }, []);

  const handleDeleteComment = useCallback(async (commentId) => {
    await postAPI.deleteComment(commentId);

    dispatch({
      type: DELETE_COMMENT,
      payload: {
        commentId,
      },
    });
  }, []);

  const handleChangeUsername = useCallback(({ userId, username }) => {
    dispatch({ type: CHANGE_USERNAME, payload: { userId, username } });
  }, []);

  const handleCreateLike = useCallback(({ postId, createdLike }) => {
    dispatch({ type: CREATE_LIKE, payload: { postId, createdLike } });
  }, []);

  const handleDeleteLike = useCallback(({ postId, userId }) => {
    dispatch({ type: DELETE_LIKE, payload: { postId, userId } });
  }, []);

  useEffect(() => {
    const initPosts = async () => {
      await handleGetPosts();
    };
    // TODO: 이거 언제언제 실행되는지 확인하기
    if (!isInitialized) {
      setIsInitialized(true);
      initPosts();
    }
  }, [handleGetPosts, isInitialized]);

  return (
    <PostContext.Provider
      value={{
        ...post,
        isLoading,
        handleGetPosts,
        handleGetMyPosts,
        handleSetSelectedPost,
        handleCreatePost,
        handleUpdatePost,
        handleDeletePost,
        handleCreateComment,
        handleDeleteComment,
        handleChangeUsername,
        handleDeleteLike,
        handleCreateLike,
      }}
    >
      {children}
      <Modal visible={modalVisible} onClose={closeModal}>
        질문 등록 완료!
      </Modal>
    </PostContext.Provider>
  );
};

PostProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PostProvider;
